import React, { useEffect, useState } from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "../core/helpers/firebase";
import { userService } from "../core/services";
import { AppDispatch, RootState } from "../core/redux/store";
import { setUser } from "../core/redux/userSlice";
import { utils } from "../core/helpers";
import ImageWithBasePath from "../core/common/imageWithBasePath";
import {useMsal} from "@azure/msal-react";
import {loginRequest} from "../core/helpers/microsoftConfig";

type PasswordField = "password";

const Login: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const [errorMessage, setErrorMessage] = useState<string>("");

  const {instance: microsoftInstance, accounts: microsoftAccounts} = useMsal()

  useEffect(() => {
    if (!!utils.getPasswordToken() && !!user) {
      navigate("/");
    }
  }, [navigate, user]);

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  /**
   * Handles traditional Email/Password login.
   */
  const handleEmailPasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage("");

    const response = await userService.login(email, password);

    // 1. CHECK FOR response.user to determine success
    if (response.user) {
      // SUCCESS: User object found in response
      dispatch(setUser(response.user));
      utils.setStore("passwordToken", response.token);
      navigate("/");
    } else {
      // FAILURE: Generic error
      setErrorMessage("Login failed. Please verify your credentials.");
    }
  };

  /**
   * Handles Google OAuth login.
   */
  const handleGoogleLogin = async () => {
    setErrorMessage("");
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const loggedInUser = result.user;

      const response = await userService.loginGoogle({
        user: loggedInUser,
        // @ts-ignore
        idToken: result._tokenResponse.oauthIdToken,
      });

      if (response.message === "Invalid token or user not found") {
        await auth.signOut();
        setErrorMessage(
            "@ogr.iuc.edu.tr veya @iuc.edu.tr uzantılı e-posta adresi ile giriş yapmanız gerekmektedir."
        );
      } else {
        dispatch(setUser(response.user));
        utils.setStore("passwordToken", response.token);
        navigate("/");
      }
    } catch (error) {
      console.error("Google Login Failed:", error);
      setErrorMessage("An error occurred during sign-in. Please try again.");
    }
  };

  const handleMicrosoftLogin = () => {
      microsoftInstance.loginRedirect(loginRequest)
  }

    useEffect(() => {

        const f = async () => {
            const response = await userService.loginMicrosoft({
                user: microsoftAccounts[0],
                idToken: microsoftAccounts[0].idToken
            })
            dispatch(setUser(response.user));
            utils.setStore("passwordToken", response.token);
            navigate("/");
        }

        if (microsoftAccounts.length > 0){
            f()
        }
    }, [microsoftAccounts]);

  const handleQrLoginNavigate = () => {
    navigate("/qr-login");
  };


  return (
      <>
        <div className="main-wrapper">
          <div className="login-content">
            <div className="row">
              {/* Login Banner */}
              <div className="col-md-6 login-bg d-none d-md-flex">
                <div
                    className="login-banner-content"
                    style={{
                      marginLeft: "15%",
                    }}
                >
                  <div className="login-banner">
                    <ImageWithBasePath
                        src="assets/img/auth/auth-1.svg"
                        className="img-fluid"
                        alt="Welcome Banner"
                    />
                  </div>
                  <div className="mentor-course text-center">
                    <h3 className="mb-2">
                      ApiLingua <br />
                      Adaptive English Learning Platform
                    </h3>
                  </div>
                </div>
              </div>
              {/* /Login Banner */}

              <div className="col-md-6 login-wrap-bg">
                {/* Login Form Wrapper */}
                <div className="login-wrapper">
                  <div className="loginbox">
                    <div className="w-100">
                      <h1 className="fs-32 fw-bold topic">
                        Sign into Your Account
                      </h1>

                      {/* Email/Password Form */}
                      <form onSubmit={handleEmailPasswordSubmit} className="mb-3">
                        {/* Email Field */}
                        <div className="mb-3 position-relative">
                          <label className="form-label">
                            Email<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="position-relative">
                            <input
                                type="email"
                                className="form-control form-control-lg"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                            <span>
                            <i className="isax isax-sms input-icon text-gray-7 fs-14" />
                          </span>
                          </div>
                        </div>

                        {/* Password Field */}
                        <div className="mb-3 position-relative">
                          <label className="form-label">
                            Password <span className="text-danger ms-1">*</span>
                          </label>
                          <div className="position-relative" id="passwordInput">
                            <input
                                type={
                                  passwordVisibility.password ? "text" : "password"
                                }
                                className="form-control form-control-lg pass-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <span
                                className={`isax toggle-passwords fs-14 ${
                                    passwordVisibility.password
                                        ? "isax-eye"
                                        : "isax-eye-slash"
                                }`}
                                onClick={() => togglePasswordVisibility("password")}
                            ></span>
                          </div>
                        </div>

                        {/* Login Button */}
                        <div className="d-grid mt-4">
                          <button
                              className="btn btn-secondary btn-lg"
                              type="submit"
                          >
                            Login <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </form>
                      {/* End Email/Password Form */}

                      {/* Separator for Email/Password */}
                      <div className="separator-text my-4">
                        <hr className="hr-text" data-content="OR" />
                      </div>

                      {/* QR Login Redirect */}
                      <div className="d-grid mb-3">
                        <button
                          type="button"
                          onClick={handleQrLoginNavigate}
                          className="btn btn-outline-secondary btn-lg"
                        >
                          Login with QR Code
                        </button>
                      </div>
                      {/* End QR Login Redirect */}

                      {/* Google Login Section */}
                      <div className="d-grid mb-4">
                        <button
                            type="button"
                            onClick={handleGoogleLogin}
                            className="btn btn-light d-flex align-items-center justify-content-center btn-lg"
                        >
                          <ImageWithBasePath
                              src="assets/img/icons/google.svg"
                              alt="Google"
                              className="me-2"
                          />
                          Sign in with Google
                        </button>
                      </div>
                      {/* End Google Login Section */}
                        <div className="d-grid mb-4">
                            <button
                                type="button"
                                onClick={handleMicrosoftLogin}
                                className="btn btn-light d-flex align-items-center justify-content-center btn-lg"
                            >
                                <ImageWithBasePath
                                    src="assets/img/icons/microsoft.svg"
                                    alt="Google"
                                    className="me-2"
                                />
                                Sign in with Microsoft
                            </button>
                        </div>

                      {/* Display error message if it exists */}
                      {errorMessage && (
                          <div className="mt-3 mb-3 text-center text-danger">
                            {errorMessage}
                          </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* /Login Form Wrapper */}
              </div>
            </div>
          </div>
        </div>
        {/* /Main Wrapper */}
      </>
  );
};

export default Login;
